import { bytesToBase64Url, toBufferSource } from './encoding.js'

/**
 * Domain-separated key derivation — PRD §7.4, §14.1, §14.7.
 *
 * One implementation of "a 256-bit HMAC over the deployment secret, separated by a versioned
 * domain and a list of identity parts". Everything that must be *derivable* rather than stored
 * — the recipient capability token and the envelope key — is a thin naming of this primitive, so
 * a second derivation cannot drift from the first: the domain string is part of what is signed
 * (not a prefix a caller could get wrong), and the parts are joined with `\u0000` so
 * `['ab','c']` and `['a','bc']` are different inputs.
 *
 * A deployment that has no secret must not mint anything: the derivation fails closed with
 * {@link SecretMissingError} rather than deriving from an empty string.
 */
export class SecretMissingError extends Error {
  constructor(message = 'deployment secret is not configured') {
    super(message)
    this.name = 'SecretMissingError'
  }
}

const encoder = new TextEncoder()

/**
 * The raw 256-bit derivation. The caller names the domain; the same secret + domain + parts
 * always produce the same 32 bytes, and a different domain produces unrelated bytes.
 */
export async function deriveDomainBytes(
  secret: string,
  domain: string,
  parts: readonly string[],
): Promise<Uint8Array> {
  if (secret === '') throw new SecretMissingError()
  const key = await crypto.subtle.importKey(
    'raw',
    toBufferSource(encoder.encode(secret)),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  )
  const message = encoder.encode([domain, ...parts].join('\u0000'))
  const signature = await crypto.subtle.sign('HMAC', key, toBufferSource(message))
  return new Uint8Array(signature)
}

/** The same derivation in the unpadded base64url form bearer tokens are carried in. */
export async function deriveDomainToken(
  secret: string,
  domain: string,
  parts: readonly string[],
): Promise<string> {
  return bytesToBase64Url(await deriveDomainBytes(secret, domain, parts))
}
